<template>
  <v-dialog v-model="internalDialog" max-width="1200px" persistent scrollable>
    <v-card>
      <!-- Header -->
      <v-card-title class="pa-0">
        <v-toolbar 
          :color="currentStep === 'selection' ? 'primary' : 'primary'" 
          dark 
          flat
        >
          <v-icon class="mr-2">
            {{ currentStep === 'selection' ? 'mdi-file-chart' : 'mdi-chart-line' }}
          </v-icon>
          <v-toolbar-title>
            {{ currentStep === 'selection' ? 'Seleccionar Reporte' : selectedReportTitle }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          
          <!-- Back button when viewing report -->
          <v-btn 
            v-if="currentStep === 'report'" 
            icon 
            @click="goBackToSelection"
            class="mr-2"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          
          <!-- Excel download button -->
          <v-btn 
            v-if="currentStep === 'report'" 
            icon 
            @click="downloadExcel"
            class="mr-2"
          >
            <v-icon>mdi-microsoft-excel</v-icon>
          </v-btn>
          
          <!-- Close button -->
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="pa-0" style="height: 600px;">
        <!-- Step 1: Report Selection -->
        <div v-if="currentStep === 'selection'" class="pa-6">
          <div class="text-center mb-6">
            <h2 class="text-h5 mb-2"> 📊 ¿Qué reporte deseas visualizar? 📝</h2>
            <p class="text-subtitle-1 grey--text">
              Selecciona el tipo de análisis que deseas generar para 
                  <strong class="primary--text">{{ workerName }} </strong>
            </p>
          </div>

          <v-row justify="center">
            <v-col 
              v-for="report in reportOptions" 
              :key="report.id"
              cols="12" 
              md="4"
              class="d-flex"
            >
              <v-card 
                class="report-card flex-grow-1" 
                hover
                @click="selectReport(report)"
                :class="{ 'elevation-8': true }"
              >
                <div class="text-center pa-6">
                  <v-avatar size="80" :color="report.color" class="mb-4">
                    <v-icon size="40" color="white">{{ report.icon }}</v-icon>
                  </v-avatar>
                  
                  <h3 class="text-h6 mb-2">{{ report.title }}</h3>
                  <p class="text-body-2 grey--text mb-4">{{ report.description }}</p>
                  
                  <v-btn 
                    :color="report.color" 
                    rounded 
                    depressed
                    block
                    dark
                  >
                    <v-icon left>mdi-eye</v-icon>
                    Ver Reporte
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Step 2: Report View -->
        <div v-else-if="currentStep === 'report'">
          <!-- Filters Section -->
          <v-card flat class="pa-4 grey lighten-5">
            <v-row align="center">
              <v-col cols="12" md="3">
                <s-date
                  label="Fecha Inicio"
                  v-model="currentFilter.InitDate"
                  :key="`init-${dateKey}`"
                />
              </v-col>
              <v-col cols="12" md="3">
                <s-date
                  label="Fecha Fin"
                  v-model="currentFilter.EndDate"
                  :key="`end-${dateKey}`"
                />
              </v-col>
              <v-col cols="12" md="2" >
                <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                        <v-btn
                            icon
                            color="primary"
                            @click="loadReportData"
                            :loading="loading"
                            v-bind="attrs"
                            v-on="on"
                            elevation="2"
                            class="rounded-xl"
                        >
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                        </template>
                        <span>Consultar</span>
                    </v-tooltip>
                </v-col>

              
            </v-row>
          </v-card>

          <!-- Report Data Table -->
          <div class="pa-4">
            <v-data-table
              :headers="reportHeaders"
              :items="reportItems"
              :loading="loading"
              :items-per-page="15"
              item-key="Line"
              dense
              fixed-header
              height="400"
              class="elevation-2"
            >
              <template v-slot:loading>
                <div class="text-center pa-4">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="50"
                  ></v-progress-circular>
                  <p class="mt-2">Cargando datos del reporte...</p>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="text-center pa-8">
                  <v-icon size="80" color="grey lighten-2" class="mb-4">
                    mdi-file-search-outline
                  </v-icon>
                  <h3 class="text-h6 grey--text mb-2">No hay datos disponibles</h3>
                  <p class="grey--text">
                    No se encontraron registros para los filtros seleccionados
                  </p>
                </div>
              </template>
            </v-data-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import _sHmnHoursWorkedService from '@/services/HumanResource/HmnHoursWorkedService.js';

export default {
  name: 'ReportModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    wkrId: {
      type: Number,
      default: 0
    },
    workerName: {
      type: String,
      default: ''
    },
    initDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentStep: 'selection', // 'selection' or 'report'
      selectedReport: null,
      selectedReportTitle: '',
      loading: false,
      dateKey: 0,
      
      // Report data
      reportHeaders: [],
      reportItems: [],
      
      // Current filter for the selected report
      currentFilter: {
        InitDate: this.$fun.getDate(),
        EndDate: this.$fun.getDate(),
        WkrID: 0,
        TypeReport: 1
      },

      // Report options
      reportOptions: [
        {
          id: 1,
          title: 'Horas Trabajadas',
          description: 'Reporte detallado de las horas trabajadas por el empleado',
          icon: 'mdi-clock-outline',
          color: 'blue',
          typeReport: 1,
          useWeekLogic: true // Usa la lógica de semana
        },
        {
          id: 2,
          title: 'Distribución',
          description: 'Reporte de distribución de horas trabajadas por empleado',
          icon: 'mdi-account-check',
          color: 'green',
          typeReport: 2,
          useWeekLogic: false // No usa lógica de semana
        },
        {
          id: 3,
          title: 'Horas Diarias',
          description: 'Reporte de horas trabajadas por día para el empleado',
          icon: 'mdi-chart-bar',
          color: 'purple',
          typeReport: 3,
          useWeekLogic: false, // No usa lógica de semana
          customEndpoint: false // Usa endpoint diferente
        }
      ]
    }
  },
  computed: {
    internalDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeModal();
      } else {
        this.resetModal();
      }
    },
  },
  methods: {
    initializeModal() {
      this.currentStep = 'selection';
      this.currentFilter.WkrID = this.wkrId;
      this.resetDates();
    },

    resetModal() {
      this.currentStep = 'selection';
      this.selectedReport = null;
      this.selectedReportTitle = '';
      this.reportHeaders = [];
      this.reportItems = [];
      this.loading = false;
    },

    selectReport(report) {
      this.selectedReport = report;
      this.selectedReportTitle = report.title;
      this.currentFilter.TypeReport = report.typeReport;
      this.currentStep = 'report';
      
      // Load initial data
      this.loadReportData();
    },

    goBackToSelection() {
      this.currentStep = 'selection';
      this.selectedReport = null;
      this.reportHeaders = [];
      this.reportItems = [];
    },

    async loadReportData() {
      if (!this.selectedReport) return;

      this.loading = true;
      
      try {
        let response;
        
        if (this.selectedReport.customEndpoint) {
          // Para el reporte especial, usar endpoint diferente
          // response = await _sCustomReportService.list(this.currentFilter, this.$fun.getUserID());
          // Por ahora simular datos
          response = { data: [] };
          this.$fun.alert('Reporte especial en desarrollo', 'info');
        } else {
          if (this.currentFilter.TypeReport === 3) {
            response = await _sHmnHoursWorkedService.listDaily(this.currentFilter, this.$fun.getUserID());
          }
          else {
            response = await _sHmnHoursWorkedService.list(this.currentFilter, this.$fun.getUserID());
          }
            
        }

        const data = response.data;

        if (Array.isArray(data) && data.length > 0) {
          // Generar headers dinámicamente
          const excludeFields = ['DIF. ENTRADA', 'DIF. SALIDA'];
          
          this.reportHeaders = Object.keys(data[0])
            .filter(key => !excludeFields.includes(key.toUpperCase()))
            .map(key => ({
              text: key.replace(/_/g, ' ').toUpperCase(),
              value: key,
              sortable: true
            }));
        } else {
          this.reportHeaders = [];
        }
        
        this.reportItems = data;

      } catch (error) {
        console.error('Error al cargar reporte:', error);
        this.$fun.alert('Error al cargar los datos del reporte', 'error');
        this.reportHeaders = [];
        this.reportItems = [];
      } finally {
        this.loading = false;
      }
    },

    resetDates() {
      this.currentFilter.InitDate = this.initDate || this.$fun.getDate();
      this.currentFilter.EndDate = this.endDate || this.$fun.getDate();
      this.dateKey++;
    },

    downloadExcel() {
      if (!this.selectedReport) return;

      try {
        if (this.selectedReport.customEndpoint) {
          // Para reporte especial
          this.$fun.alert('Descarga de Excel para reporte especial en desarrollo', 'info');
        } else {
            if(this.currentFilter.TypeReport === 3){
                _sHmnHoursWorkedService.excelDaily(this.currentFilter, this.$fun.getUserID())
                .then(response => {
                  this.$fun.downloadFile(
                    response.data, 
                    this.$const.TypeFile.EXCEL, 
                    `${this.selectedReport.title}_${this.workerName.replace(/\s+/g, '_')}`
                  );
                });
            }
            else
            {
                _sHmnHoursWorkedService.excel(this.currentFilter, this.$fun.getUserID())
                .then(response => {
                this.$fun.downloadFile(
                    response.data, 
                    this.$const.TypeFile.EXCEL, 
                    `${this.selectedReport.title}_${this.workerName.replace(/\s+/g, '_')}`
                );
                });
            }
        }
      } catch (error) {
        console.error('Error al descargar Excel:', error);
        this.$fun.alert('Error al generar el archivo Excel', 'error');
      }
    },

    closeDialog() {
      this.internalDialog = false;
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.report-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.report-card:hover {
  transform: translateY(-4px);
}

.v-data-table {
  border-radius: 8px;
}

.v-toolbar {
  border-radius: 0 !important;
}
</style>